import React, { useRef, useEffect } from "react";

// Dummy event data for the GIVF event carousel
const eventCarouselData = [
  {
    image: "/images/events/resume-mentorship.jpg",
    title: "Resume Writing Mentorship",
    location: "Virtual",
    duration: "90 mins",
    volunteers: "25-100",
    description:
      "Well-written resumes improve interview call-back rates by 40%. Mentorship in writing resumes helps job seekers articulate skills, especially for first-gen professionals.",
    price: "INR 300 per volunteer",
    availability: "Available in 16 locations",
  },
  {
    image: "/images/events/crossword-creators.jpg",
    title: "Science Crossword Creators",
    location: "In-Office",
    duration: "90 mins",
    volunteers: "50-100",
    description:
      "Integrating science content into puzzles reinforces learning through gamification—a method shown by Edutopia to improve academic engagement by 17%.",
    price: "INR 425 per volunteer",
    availability: "Available in 16 locations",
  },
  {
    image: "/images/events/skill-up.jpg",
    title: "Skill Up: Career Readiness Series",
    location: "In-Office",
    duration: "90 mins",
    volunteers: "25-100",
    description:
      "Skill development is essential for youth employability and long-term economic stability. This series offers practical, hands-on upskilling workshops.",
    price: "INR 600 per volunteer",
    availability: "Available in 16 locations",
  },
  {
    image: "/images/events/study-corner.jpg",
    title: "Decorate a Study Corner for a Child",
    location: "In-Office",
    duration: "90 mins",
    volunteers: "25-100",
    description:
      "Many children in underserved homes lack dedicated, inspiring learning environments. Enhancing their study space fosters focus and ownership.",
    price: "INR 900 per volunteer",
    availability: "Available in 16 locations",
  },
  {
    image: "/images/events/play-spaces.jpg",
    title: "Design Sustainable Play Spaces",
    location: "Outdoor",
    duration: "90 mins",
    volunteers: "50-100",
    description:
      "Recycled-material playscapes provide both physical activity and eco-education, especially in urban slums and rural schools.",
    price: "INR 1150 per volunteer",
    availability: "Available in 16 locations",
  },
  {
    image: "/images/events/digital-declutter.jpg",
    title: "Digital Declutter Day",
    location: "Virtual",
    duration: "90 mins",
    volunteers: "25-200",
    description:
      "Unused digital data and clutter in email and cloud storage contribute to energy consumption and decreased productivity. Streamline digital storage and boost efficiency!",
    price: "INR 10000 per event",
    availability: "Available in 16 locations",
  },
  {
    image: "/images/events/hygiene-pack.jpg",
    title: "Personal Hygiene Pack Drive",
    location: "In-Office",
    duration: "90 mins",
    volunteers: "30-100",
    description:
      "Poor personal hygiene contributes to significant morbidity rates, especially in children. This drive helps provide essential hygiene kits.",
    price: "INR 750 per volunteer",
    availability: "Available in 16 locations",
  },
  {
    image: "/images/events/quiz-science.jpg",
    title: "Quiz IT: Science Edition",
    location: "In-Office",
    duration: "90 mins",
    volunteers: "25-100",
    description:
      "Engage teams in STEM-based quiz competitions to foster collaborative learning and friendly rivalry.",
    price: "INR 400 per volunteer",
    availability: "Available in 16 locations",
  },
  {
    image: "/images/events/skill-resume.jpg",
    title: "Ready for Work: Skill & Resume Building",
    location: "In-Office",
    duration: "90 mins",
    volunteers: "50-100",
    description:
      "Effective resume preparation and skill development are vital for improving interview success rates. This initiative offers targeted workshops.",
    price: "INR 550 per volunteer",
    availability: "Available in 16 locations",
  },
  {
    image: "/images/events/art-for-impact.jpg",
    title: "Art for Impact Workshop",
    location: "Outdoor",
    duration: "60 mins",
    volunteers: "30-80",
    description:
      "Channel creativity for a cause! Volunteers create digital or physical artwork to support nonprofit campaigns and awareness drives.",
    price: "INR 650 per volunteer",
    availability: "Available in 16 locations",
  },
  {
    image: "/images/events/green-drive.jpg",
    title: "Green Drive: Urban Tree Planting",
    location: "Outdoor",
    duration: "120 mins",
    volunteers: "20-60",
    description:
      "Join hands to plant saplings in city parks and public spaces, increasing urban green cover and community pride.",
    price: "INR 750 per volunteer",
    availability: "Available in 16 locations",
  },
  {
    image: "/images/events/tech-literacy.jpg",
    title: "Tech Literacy for Seniors",
    location: "In-Office",
    duration: "90 mins",
    volunteers: "15-40",
    description:
      "Empower senior citizens with essential digital skills in a supportive, hands-on workshop setting.",
    price: "INR 500 per volunteer",
    availability: "Available in 16 locations",
  },
  {
    image: "/images/events/food-distribution.jpg",
    title: "Food Distribution Drive",
    location: "Outdoor",
    duration: "90 mins",
    volunteers: "25-120",
    description:
      "Help package and distribute nutritious meals to underserved communities, reducing food insecurity.",
    price: "INR 720 per volunteer",
    availability: "Available in 16 locations",
  },
  {
    image: "/images/events/wellness-session.jpg",
    title: "Wellness & Mindfulness Session",
    location: "Virtual",
    duration: "60 mins",
    volunteers: "25-200",
    description:
      "Certified instructors lead guided mindfulness and wellness sessions to promote mental health and resilience.",
    price: "INR 10000 per event",
    availability: "Available in 7 locations",
  },
  {
    image: "/images/events/library-build.jpg",
    title: "Build a Mini Library",
    location: "In-Office",
    duration: "120 mins",
    volunteers: "30-80",
    description:
      "Assemble and decorate mini library kits for placement in schools and community centers, fostering a love for reading.",
    price: "INR 1250 per volunteer",
    availability: "Available in 7 locations",
  },
];

const CARD_WIDTH = 340; // px (includes margin)
const VISIBLE_COUNT = 3;
const CONTAINER_WIDTH = CARD_WIDTH * VISIBLE_COUNT;

const EventCarouselSection: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  // Duplicate the events so the scroll can loop seamlessly
  const allEvents = [...eventCarouselData, ...eventCarouselData];
  useEffect(() => {
    const container = containerRef.current;
    let animationFrame: number;
    let start: number;
    let scrollLeft = 0;
    const totalWidth = CARD_WIDTH * eventCarouselData.length;
    function step(timestamp: number) {
      if (!start) start = timestamp;
      const elapsed = timestamp - start;
      // Scroll speed: 40px/sec
      scrollLeft = (elapsed * 0.04) % totalWidth;
      if (container) container.scrollLeft = scrollLeft;
      animationFrame = requestAnimationFrame(step);
    }
    animationFrame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(animationFrame);
  }, []);

  return (
    <section className="py-16 bg-[#0d1927] text-white" id="events">
      <div className="container mx-auto px-4">
        <h2 className="text-4xl md:text-5xl font-extrabold mb-6 leading-tight text-white drop-shadow-xl text-center">
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 via-pink-500 to-red-500">
            Explore Events
          </span>
        </h2>
        <h2 className="text-2xl md:text-3xl font-bold mb-10 text-center">
          300+ unique Events with Transparent all inclusive Rates
        </h2>
        <div className="relative h-[400px] max-w-[1200px] mx-auto overflow-hidden rounded-2xl shadow-xl border border-gray-100 bg-[#f8fafc] flex flex-col items-center justify-center">
          <div
            ref={containerRef}
            style={{
              width: CONTAINER_WIDTH,
              overflow: "hidden",
              height: 320,
              margin: "0 auto",
            }}
            className="relative"
          >
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                width: allEvents.length * CARD_WIDTH,
              }}
            >
              {allEvents.map((event, i) => (
                <div
                  key={i}
                  className="bg-white rounded-2xl shadow-md border border-gray-100 flex flex-col overflow-hidden mx-2 hover:shadow-2xl transition-shadow duration-200"
                  style={{
                    background: "#f8fafc",
                    minWidth: 320,
                    maxWidth: 340,
                  }}
                >
                  <img
                    src={event.image}
                    alt={event.title}
                    className="h-32 w-full object-cover rounded-t-2xl"
                  />
                  <div className="p-4 flex flex-col flex-1">
                    <div className="font-extrabold text-lg mb-1 text-gray-900 truncate">
                      {event.title}
                    </div>
                    <div className="flex flex-wrap gap-3 text-xs font-medium text-gray-600 mb-2">
                      <span className="inline-flex items-center gap-1 bg-gray-100 px-2 py-1 rounded">
                        <span role="img" aria-label="location">
                          📍
                        </span>
                        {event.location}
                      </span>
                      <span className="inline-flex items-center gap-1 bg-gray-100 px-2 py-1 rounded">
                        <span role="img" aria-label="duration">
                          ⏱
                        </span>
                        {event.duration}
                      </span>
                      <span className="inline-flex items-center gap-1 bg-gray-100 px-2 py-1 rounded">
                        <span role="img" aria-label="volunteers">
                          👥
                        </span>
                        {event.volunteers}
                      </span>
                    </div>
                    <div className="text-gray-700 text-sm mb-2 flex-1 line-clamp-3">
                      {event.description}
                    </div>
                    <div className="font-semibold text-primary text-base mb-1">
                      {event.price}
                    </div>
                    <div className="text-xs text-gray-400">
                      {event.availability}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default EventCarouselSection;
